<template>
	<div
		:id="uid"
		class="field"
		:class="{
			'is-disabled': disabled,
			'is-required': required,
			'has-error': showError
		}"
		:style="widthStyle"
	>
		<Label
			:for="`${uid}_input`"
			class="field__label"
			v-if="label"
			:labelText="label"
		/>
		<div class="field__input-container">
			<input
				class="field__input"
				autocomplete="off"
				ref="fieldinput"
				:aria-label="ariaLabel"
				:class="className"
				:id="`${uid}_input`"
				:min="min"
				:max="max"
				:name="name"
				:placeholder="placeholder"
				:required="required"
				:type="variableInputType"
				:value="dataValue"
				v-on="{
					...$listeners,
					input: updateValue,
					change: updateValue,
					keyup: enterKeyUp
				}"
				@blur="touch"
			/>
			<div class="field__trailing-container">
				<div class="field__trailing-item" v-if="showError">
					<div class="field__icon field__icon--error">
						<i class="icon icon-error-solid"></i>
					</div>
				</div>
				<div class="field__trailing-item" v-if="showInputIconButton">
					<Button
						:btnClick="cleanValue"
						:className="'btn--icon-only'"
						:iconClassName="'icon-close'"
						:iconBeforeLabel="true"
						v-if="showCleanOption"
					/>
					<Button
						:btnClick="switchVisibilityValue"
						:className="'btn--icon-only'"
						:iconClassName="'icon-show'"
						:iconBeforeLabel="true"
						v-if="isOriginalPasswordType"
					/>
				</div>
			</div>
		</div>
		<div class="field__text field__text--error" v-if="showFieldError">
			{{ error }}
		</div>
		<HelpFeedback
			:helpText="helpText"
			v-if="helpText.length"
		/>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import Label from '@/components/forms/label/label';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';
import {
	required,
	minLength,
	maxLength,
	minValue,
	maxValue,
	email,
	url,
	numeric,
	integer,
	alpha,
	alphaNum
} from 'vuelidate/lib/validators';
import _ from 'lodash';

export default {
	name: 'Input',
	components: {
		Label,
		Button,
		HelpFeedback
	},
	model: {
		prop: 'value',
		event: 'input'
	},
	props: {
		label: {
			type: String,
			default: ''
		},
		className: {
			type: String,
			default: ''
		},
		helpText: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: ''
		},
		inputType: {
			type: String,
			default: ''
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		required: {
			type: Boolean,
			default: false
		},
		value: {
			type: [String, Number],
			default: ''
		},
		maxLength: {
			type: Number,
			default: 100
		},
		minLength: {
			type: Number,
			default: 0
		},
		max: {
			type: Number,
			default: Number.MAX_VALUE
		},
		min: {
			type: Number,
			default: 0
		},
		minValue: {
			type: Number
		},
		maxValue: {
			type: Number
		},
		forceCustomErrorMessage: {
			type: String,
			default: ''
		},
		isNegativeAllow: {
			type: Boolean,
			default: true
		},
		showFieldTextError: {
			type: Boolean,
			default: true
		},
		showIconCleanOption: {
			type: Boolean,
			default: false
		},
		phoneTemplate: {
			type: String,
			default: ''
		},
		width: {
			type: [Number],
			default: undefined
		}
	},
	data() {
		return {
			dataValue: '',
			variableInputType: this.inputType,
			isMounted: false
		};
	},
	computed: {
		showInputIconButton() {
			return !!this.dataValue;
		},
		isOriginalPasswordType() {
			return this.inputType.toLowerCase() === 'password';
		},
		showCleanOption() {
			return this.showIconCleanOption && !this.isOriginalPasswordType;
		},
		showError() {
			return !!this.error;
		},
		showFieldError() {
			return this.showError && this.showFieldTextError;
		},
		error() {
			if (this.forceCustomErrorMessage)
				return this.forceCustomErrorMessage;

			if (_.get(this.$v, 'dataValue.$anyError', false)) {
				const value = this.$v.dataValue;
				let msg = '';

				if (value.required === false) {
					msg = 'is required';
				} else if (value.minLength === false) {
					msg = `should have at least ${this.minLength} letters`;
				} else if (value.maxLength === false) {
					msg = `should have no more than ${this.maxLength} letters`;
				} else if (value.minValue === false) {
					msg = `should have at least ${this.minValue}`;
				} else if (value.maxValue === false) {
					msg = `should have no more than ${this.maxValue}`;
				} else if (value.email === false) {
					msg = 'is not in a valid format';
				} else if (value.alpha === false) {
					msg = 'accepts only alphabet characters';
				} else if (value.alphaNum === false) {
					msg = 'accepts only alphanumerics';
				} else if (value.numeric === false) {
					msg = 'accepts only numerics';
				} else if (value.integer === false) {
					msg = 'accepts positive and negative integers.';
				} else if (value.url === false) {
					msg = 'is not a valid Url';
				}

				return `${this.label || 'Field'} ${msg}`;
			}

			return '';
		},
		widthStyle() {
			if (this.width && typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			}
			return {};
		}
	},
	validations() {
		let result = {};
		if (this.required) {
			result.dataValue = {
				required
			};
		}
		if (this.minLength) {
			result.dataValue = {
				...result.dataValue,
				minLength: minLength(this.minLength)
			};
		}
		if (this.maxLength) {
			result.dataValue = {
				...result.dataValue,
				maxLength: maxLength(this.maxLength)
			};
		}
		if (this.maxValue) {
			result.dataValue = {
				...result.dataValue,
				maxValue: maxValue(this.maxValue)
			};
		}
		if (this.minValue || this.minValue === 0) {
			result.dataValue = {
				...result.dataValue,
				minValue: minValue(this.minValue)
			};
		}
		if (this.inputType.toLowerCase() === 'numeric') {
			result.dataValue = {
				...result.dataValue,
				numeric
			};
		}
		if (this.inputType.toLowerCase() === 'integer') {
			result.dataValue = {
				...result.dataValue,
				integer
			};
		}
		if (this.inputType.toLowerCase() === 'email') {
			result.dataValue = {
				...result.dataValue,
				email
			};
		}
		if (this.inputType.toLowerCase() === 'url') {
			result.dataValue = {
				...result.dataValue,
				url
			};
		}
		if (this.inputType.toLowerCase() === 'alpha') {
			result.dataValue = {
				...result.dataValue,
				alpha
			};
		}
		if (this.inputType.toLowerCase() === 'alphaNum') {
			result.dataValue = {
				...result.dataValue,
				alphaNum
			};
		}
		return { ...result };
	},
	mounted() {
		this.isMounted = true;
	},
	methods: {
		touch() {
			if (this.isMounted) this.$v.dataValue?.$touch();
		},
		switchVisibilityValue() {
			this.variableInputType =
				this.variableInputType === 'password' ? 'text' : 'password';
		},
		formatPhoneNumber() {
			const x = this.dataValue
				.replace(/[^0-9]/g, '')
				.match(/(\d{0,3})(\d{0,3})(\d{0,4})/);

			this.dataValue = !x[2]
				? x[1]
				: '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');

			if (this.dataValue !== this.value) {
				this.emitEvent(this.dataValue);
			}
		},
		cleanValue() {
			this.$emit('input', '');
		},
		emitEvent(value, eventName) {
			eventName = eventName || 'input';
			if (
				this.inputType.toLowerCase() === 'number' &&
				value &&
				eventName === 'input'
			) {
				value = Number(value);

				if (!this.isNegativeAllow && Math.sign(value) === -1) {
					value = Math.abs(value);
				}
			}
			this.$emit(eventName, value);
		},
		enterKeyUp(e) {
			if (e.keyCode === 13) {
				this.$emit('enterKeyUp', e);
			}
		},
		updateValue(e) {
			this.emitEvent(e.target.value);
		}
	},
	watch: {
		error: {
			immediate: true,
			handler(val) {
				this.emitEvent({ [this.name]: !!val }, 'hasError');
			}
		},
		value: {
			immediate: true,
			handler(val) {
				this.dataValue = val;
				if (this.inputType === 'phone') {
					this.formatPhoneNumber();
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
	* Field Input styles
	* Can use SCSS variables = TRUE
*/

@import '@/scss/5-objects/_form.scss';

.field {
	position: relative;
	//width: 270px;

	&__input-container {
		position: relative;
		@include textfield;
		display: flex;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__input {
		appearance: none;
		outline: none;
		border: none;
		@include input-height;
		cursor: pointer;
		font-size: $input-font-size;
		text-overflow: ellipsis;
		padding: $input-padding-y $input-padding-x;
		width: 100%;
		background-color: transparent;
		border-radius: $form-border-radius;
	}

	&__trailing-container {
		display: flex;
		align-items: center;
		font-size: $input-font-size + 2;

		.field__trailing-item.has-spinner {
			.btn--icon-only {
				min-width: auto;
				margin-right: $gutter-sm;
			}
		}
	}

	&__icon {
		padding-right: $input-padding-x;
	}

	&__icon--error {
		display: none;
	}
}

/*------------------------------------*\
  #ERROR
\*------------------------------------*/

.has-error {
	.field__input-container {
		border-color: $ui-negative;
	}

	.field__icon--error{
		display: block;
		color: $ui-negative;
	}
	.field__text--error {
		display: block;
		font-size: $label-font-size - 2;
		margin-top: $gutter-sm;
		color: $ui-negative;
	}
}
</style>
